import { capitalize } from '@quillbot/design-system';
import {
  Dispatch,
  createContext,
  useReducer,
  useContext,
  PropsWithChildren,
  Context,
  Reducer,
} from 'react';
import {
  Provider,
  ReactReduxContextValue,
  createDispatchHook,
  createSelectorHook,
  createStoreHook,
} from 'react-redux';
import { Action, AnyAction, createStore } from 'redux';

export function createReactContextProvider<T>(name: string) {
  const StoreContext = createContext<T | null>(null);
  StoreContext.displayName = `${name}Context`;
  const StoreProvider = ({ children, value }: PropsWithChildren<{ value: T }>) => (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
  StoreProvider.displayName = `${name}Provider`;
  function useValue(): T {
    const v = useContext(StoreContext);
    if (!v) {
      throw new Error(`use${name}Context must be used within ${name}Provider`);
    }
    return v;
  }
  return {
    Context: StoreContext,
    Provider: StoreProvider,
    useValue,
  };
}

export function createReactContextStore<State, StateAction>(
  name: string,
  reducer: (state: State, action: StateAction) => State
) {
  const storeName = capitalize(name);

  const { Provider: StateProvider, useValue: useState } = createReactContextProvider<State>(
    `${storeName}State`
  );

  const { Provider: DispatchProvider, useValue: useDispatch } = createReactContextProvider<
    Dispatch<StateAction>
  >(`${storeName}Dispatch`);

  function StoreProvider({ children, value }: PropsWithChildren<{ value: State }>) {
    const [state, dispatch] = useReducer(reducer, value);
    return (
      <StateProvider value={state}>
        <DispatchProvider value={dispatch}>{children}</DispatchProvider>
      </StateProvider>
    );
  }

  StoreProvider.displayName = `${storeName}Provider`;

  function useStore() {
    return [useState(), useDispatch()];
  }
  return { Provider: StoreProvider, useState, useDispatch, useStore };
}

// @TODO: check if this is completely correct and replace with all implementations
export function createReduxContextStore<State, StateAction>(reducer: Reducer<State, StateAction>) {
  const context: Context<ReactReduxContextValue<State, AnyAction>> = createContext(
    null
  ) as unknown as Context<ReactReduxContextValue<State, AnyAction>>;

  const useStore = createStoreHook(context);
  const useDispatch = createDispatchHook(context);
  const useState = createSelectorHook(context);

  const store = createStore(reducer as any);

  function StoreProvider({ children }: PropsWithChildren<{}>) {
    return (
      <Provider context={context} store={store}>
        {children}
      </Provider>
    );
  }

  return {
    context,
    store,
    useStore,
    useState,
    useDispatch,
    Provider: StoreProvider,
  };
}
